import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import PasswordInput from "./PasswordInput";
import api from "@/lib/api";

/**
 * AccountSecurityDialog — minimal self-service password change.
 * Opens from the header name/email in Layout. Reuses existing JWT auth;
 * the caller's session is preserved on success (stateless token).
 */
const AccountSecurityDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) {
      toast.error("Please fill in every field.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from the current one.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
        confirm_new_password: confirm,
      });
      toast.success("Password updated. Your session stays signed in.");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}
    >
      <DialogContent
        data-testid="account-security-dialog"
        className="bg-obsidian border border-seam text-textmain max-w-md shadow-2xl shadow-black/60 backdrop-blur-none"
        style={{ backgroundColor: "#0B132B" }}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-champagne">
            <ShieldCheck className="w-4 h-4" strokeWidth={1.6} />
            <span className="uppercase tracking-widest text-[10px]">Account Security</span>
          </div>
          <DialogTitle className="text-textmain font-heading text-2xl mt-1">
            Change your password
          </DialogTitle>
          <DialogDescription className="text-textmuted">
            You will stay signed in after the change completes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="change-password-form">
          <div>
            <label className="label-eyebrow block mb-2 text-[10px]" htmlFor="current-pw">
              Current password
            </label>
            <PasswordInput
              id="current-pw"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              testid="current-password-input"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2 text-[10px]" htmlFor="new-pw">
              New password
            </label>
            <PasswordInput
              id="new-pw"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              testid="new-password-input"
              autoComplete="new-password"
              placeholder="At least 6 characters"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2 text-[10px]" htmlFor="confirm-pw">
              Confirm new password
            </label>
            <PasswordInput
              id="confirm-pw"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              testid="confirm-new-password-input"
              autoComplete="new-password"
              placeholder="Retype the new password"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-seam">
            <button
              type="button"
              onClick={() => { if (!busy) { reset(); onOpenChange(false); } }}
              data-testid="change-password-cancel"
              disabled={busy}
              className="text-xs px-3 py-2 rounded-md border border-seam text-textmuted hover:text-textmain disabled:opacity-50"
            >
              <X className="inline w-3.5 h-3.5 mr-1" /> Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="change-password-submit"
              className="text-xs px-3 py-2 rounded-md bg-champagne text-obsidian hover:bg-champagne-soft disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              Change password
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSecurityDialog;
